/**
 * Superadmin-only. Live playback-incident forensics for a single venue.
 *
 * Wired to GET /api/analytics/admin/venue/forensics?email=<email>&hours=48.
 * Purpose: when a venue calls "playback stops after every song", we can
 * type their email in here and instantly see what their tablet's
 * telemetry is saying — which stall reason is firing, which UA the
 * device is running, and whether the JS ErrorBoundary is catching
 * anything. Before this page, the fastest way was mongosh.
 */
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Loader2, Search, AlertTriangle, Monitor, Activity, Bug, ShieldOff, Heart, Ban, TrendingUp, Wifi } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTO_RECOVERY_LABELS = {
  auto_resume: 'Silent pause resumed',
  wake_lock_lost: 'Wake-lock churn',
  net_recovered_resume: 'Network drop resumed',
  ai_recover_reload: 'AI stream reloaded (self-healed)',
};
const UNRECOVERABLE_LABELS = {
  unplayable_auto_skip: 'Unplayable track skipped',
  ai_unplayable_skip: 'AI track skipped (3 reloads failed)',
};

const RollupList = ({ title, Icon, colour, entries, metricKey, onJump, emptyMsg }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-2.5 bg-white/[0.04] text-[11px] uppercase tracking-widest text-white/70 flex items-center gap-2">
      <Icon className="w-3.5 h-3.5" style={{ color: colour }} />
      {title}
    </div>
    {entries.length === 0 ? (
      <p className="text-xs text-white/40 px-4 py-6 text-center">{emptyMsg}</p>
    ) : (
      <div className="divide-y divide-white/5 max-h-[280px] overflow-y-auto">
        {entries.map((e) => (
          <button
            key={e.host_id + metricKey}
            onClick={() => onJump(e.email)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/5 text-left transition-colors"
            data-testid={`rollup-jump-${metricKey}-${e.host_id}`}
            title={`Look up ${e.email}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white font-semibold truncate">
                {e.venue_name || e.name || e.email || '(unknown)'}
              </div>
              <div className="text-[11px] text-white/40 truncate">{e.email}</div>
            </div>
            <span className="min-w-[3ch] text-right font-bold flex-shrink-0" style={{ color: colour }}>
              {e[metricKey]}
            </span>
          </button>
        ))}
      </div>
    )}
  </div>
);

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const fmtUA = (ua) => {
  if (!ua) return 'unknown';
  const parts = [];
  if (/Android/i.test(ua)) parts.push('Android');
  else if (/iPhone|iPad|iOS/i.test(ua)) parts.push('iOS');
  else if (/Windows/i.test(ua)) parts.push('Windows');
  else if (/Macintosh|Mac OS/i.test(ua)) parts.push('macOS');
  if (/Chrome\/[\d.]+/i.test(ua)) parts.push('Chrome');
  else if (/Safari/i.test(ua)) parts.push('Safari');
  else if (/Firefox/i.test(ua)) parts.push('Firefox');
  const modelMatch = ua.match(/;\s*([^;)]+?)\s+Build\//);
  if (modelMatch) parts.push(modelMatch[1]);
  return parts.length ? parts.join(' · ') : ua.slice(0, 60);
};

const AdminForensicsPage = () => {
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState(48);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [aiRollup, setAiRollup] = useState(null);
  const [aiRollupLoading, setAiRollupLoading] = useState(false);

  const loadRollup = useCallback(async () => {
    setRollupLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/admin/stability/rollup`, { params: { hours: 24, limit: 15 } });
      setRollup(res.data);
    } catch (err) {
      // Silent — rollup is best-effort; single-venue lookup is the main flow
      // eslint-disable-next-line no-console
      console.warn('rollup fetch failed:', err?.message);
    } finally {
      setRollupLoading(false);
    }
  }, []);

  const loadAiRollup = useCallback(async () => {
    setAiRollupLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/admin/ai-stability/rollup`, { params: { hours: 24, limit: 15 } });
      setAiRollup(res.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('ai rollup fetch failed:', err?.message);
    } finally {
      setAiRollupLoading(false);
    }
  }, []);

  useEffect(() => { loadRollup(); loadAiRollup(); }, [loadRollup, loadAiRollup]);

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/analytics/admin/venue/forensics`, {
        params: { email: email.trim(), hours },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Lookup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [email, hours]);

  const jumpToVenue = useCallback((venueEmail) => {
    setEmail(venueEmail);
    setHours(24);
    // Manually fire the search — setState is async so we can't rely on `search()` picking up the new value.
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await axios.get(`${API}/analytics/admin/venue/forensics`, {
          params: { email: venueEmail, hours: 24 },
        });
        setData(res.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Lookup failed');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-white" style={{ fontFamily: 'Outfit' }}>
            Playback Forensics
          </h1>
          <p className="text-sm text-white/50">
            Look up a venue by email to see what their tablet is reporting — stalls, JS errors, and device UAs.
          </p>
        </header>

        <form onSubmit={search} className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="forensics-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="venue@example.com"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF007F]"
            required
          />
          <select
            data-testid="forensics-hours-select"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF007F]"
          >
            <option value={2}>Last 2h</option>
            <option value={24}>Last 24h</option>
            <option value={48}>Last 48h</option>
            <option value={168}>Last 7d</option>
            <option value={720}>Last 30d</option>
          </select>
          <Button
            type="submit"
            disabled={loading || !email.trim()}
            data-testid="forensics-search-btn"
            className="bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold px-6"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Look up</span>
          </Button>
        </form>

        {/* ============================================================
             Cross-venue rollup — the 24h "who's misbehaving" dashboard
             ============================================================ */}
        <section data-testid="stability-rollup" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#FF007F]" />
              24h stability rollup — all venues
            </h2>
            <button
              onClick={loadRollup}
              disabled={rollupLoading}
              data-testid="rollup-refresh-btn"
              className="text-[11px] uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1"
            >
              {rollupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Refresh
            </button>
          </div>

          {rollup ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Auto-recoveries</div>
                  <div className="text-2xl font-extrabold text-[#22C55E] mt-1" data-testid="rollup-auto-total">
                    {rollup.totals?.auto_recoveries ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Auto-skips</div>
                  <div className="text-2xl font-extrabold text-[#FFB020] mt-1" data-testid="rollup-unrec-total">
                    {rollup.totals?.unrecoverable ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Real stalls</div>
                  <div className="text-2xl font-extrabold text-[#FF3B3B] mt-1" data-testid="rollup-stalls-total">
                    {rollup.totals?.real_stalls ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Venues active</div>
                  <div className="text-2xl font-extrabold text-white mt-1" data-testid="rollup-venues-total">
                    {rollup.totals?.venues_touched ?? 0}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <RollupList
                  title="Top auto-recovery"
                  Icon={Heart}
                  colour="#22C55E"
                  entries={rollup.by_auto_recoveries || []}
                  metricKey="auto_recoveries"
                  onJump={jumpToVenue}
                  emptyMsg="No watchdog activity in the last 24h."
                />
                <RollupList
                  title="Top auto-skips"
                  Icon={Ban}
                  colour="#FFB020"
                  entries={rollup.by_unrecoverable || []}
                  metricKey="unrecoverable"
                  onJump={jumpToVenue}
                  emptyMsg="No tracks skipped after 3 failed resumes."
                />
                <RollupList
                  title="Top real stalls"
                  Icon={AlertTriangle}
                  colour="#FF3B3B"
                  entries={rollup.by_real_stalls || []}
                  metricKey="real_stalls"
                  onJump={jumpToVenue}
                  emptyMsg="No unrecovered stalls in the last 24h."
                />
              </div>
            </>
          ) : (
            <div className="text-sm text-white/40 py-4">Loading 24h rollup…</div>
          )}
        </section>

        {/* ============================================================
             AI Booth wi-fi health — in-house library engine, isolated
             from the YouTube watchdog rollup above
             ============================================================ */}
        <section data-testid="ai-stability-rollup" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
              <Wifi className="w-4 h-4 text-[#22D3EE]" />
              24h AI booth wi-fi health — all venues
            </h2>
            <button
              onClick={loadAiRollup}
              disabled={aiRollupLoading}
              data-testid="ai-rollup-refresh-btn"
              className="text-[11px] uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1"
            >
              {aiRollupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Refresh
            </button>
          </div>

          {aiRollup ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Stream reloads</div>
                  <div className="text-2xl font-extrabold text-[#22D3EE] mt-1" data-testid="ai-rollup-reloads-total">
                    {aiRollup.totals?.reloads ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Tracks skipped</div>
                  <div className="text-2xl font-extrabold text-[#FFB020] mt-1" data-testid="ai-rollup-skips-total">
                    {aiRollup.totals?.skips ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Media errors</div>
                  <div className="text-2xl font-extrabold text-[#FF3B3B] mt-1" data-testid="ai-rollup-errors-total">
                    {aiRollup.totals?.errors ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Venues affected</div>
                  <div className="text-2xl font-extrabold text-white mt-1" data-testid="ai-rollup-venues-total">
                    {aiRollup.totals?.venues_touched ?? 0}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="px-4 py-2.5 bg-white/[0.04] text-[11px] uppercase tracking-widest text-white/70 flex items-center gap-2">
                  <Wifi className="w-3.5 h-3.5 text-[#22D3EE]" />
                  Shakiest AI-booth venues (last 24h)
                </div>
                {(aiRollup.by_venue || []).length === 0 ? (
                  <p className="text-xs text-white/40 px-4 py-6 text-center">
                    No AI-booth stalls in the last 24h. In-house library streaming is smooth. 👍
                  </p>
                ) : (
                  <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
                    <div className="hidden sm:flex items-center px-4 py-2 text-[10px] uppercase tracking-widest text-white/30">
                      <span className="flex-1">Venue</span>
                      <span className="w-16 text-right text-[#22D3EE]">Reloads</span>
                      <span className="w-14 text-right text-[#FFB020]">Skips</span>
                      <span className="w-14 text-right text-[#FF3B3B]">Errors</span>
                      <span className="w-20 text-right">Last</span>
                    </div>
                    {aiRollup.by_venue.map((v) => (
                      <button
                        key={v.host_id}
                        onClick={() => jumpToVenue(v.email)}
                        data-testid={`ai-rollup-jump-${v.host_id}`}
                        className="w-full flex items-center px-4 py-2.5 hover:bg-white/5 text-left transition-colors"
                        title={`Look up ${v.email}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white font-semibold truncate">
                            {v.venue_name || v.name || v.email || '(unknown)'}
                          </div>
                          <div className="text-[11px] text-white/40 truncate">{v.email}</div>
                        </div>
                        <span className="w-16 text-right font-bold text-[#22D3EE]">{v.reloads}</span>
                        <span className="w-14 text-right font-bold text-[#FFB020]">{v.skips}</span>
                        <span className="w-14 text-right font-bold text-[#FF3B3B]">{v.errors}</span>
                        <span className="w-20 text-right text-[11px] text-white/40">{relTime(v.last_at)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-white/40">
                Cyan reloads are healthy self-heals — the engine recovered a stalled stream. Amber skips + red errors are the wi-fi pain signals. Click a venue to open its full forensics.
              </p>
            </>
          ) : (
            <div className="text-sm text-white/40 py-4">Loading AI booth health…</div>
          )}
        </section>

        {data && (
          <div className="space-y-6" data-testid="forensics-results">
            {/* Host summary */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h2 className="text-lg font-bold text-white">{data.host?.name || 'Unknown host'}</h2>
                <span className="text-xs font-mono text-white/40">{data.host?.email}</span>
              </div>
              <div className="text-xs text-white/50 mt-1">
                {data.host?.venue_name || '—'} {data.host?.venue_code ? `· ${data.host.venue_code}` : ''}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Real stalls</div>
                  <div className="text-2xl font-extrabold text-[#FF3B3B] mt-1" data-testid="forensics-stalls-total">
                    {data.stalls?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Auto-recoveries</div>
                  <div className="text-2xl font-extrabold text-[#22C55E] mt-1" data-testid="forensics-recoveries-total">
                    {data.auto_recoveries?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Auto-skips</div>
                  <div className="text-2xl font-extrabold text-[#FFB020] mt-1" data-testid="forensics-unrec-total">
                    {data.unrecoverable?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Ads slipped</div>
                  <div className="text-2xl font-extrabold text-[#FF007F] mt-1" data-testid="forensics-ads-total">
                    {data.ads?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">JS Errors</div>
                  <div className="text-2xl font-extrabold text-[#FFB020] mt-1" data-testid="forensics-errors-total">
                    {data.errors?.total ?? 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-recoveries breakdown (Feb 2026 — watchdogs) */}
            {(data.auto_recoveries?.by_reason || []).length > 0 && (
              <section data-testid="forensics-recoveries-section">
                <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#22C55E]" />
                  Watchdog auto-recoveries
                </h3>
                <div className="rounded-xl border border-[#22C55E]/20 divide-y divide-white/5 overflow-hidden bg-[#22C55E]/[0.03]">
                  {data.auto_recoveries.by_reason.map((r) => (
                    <div key={r.reason} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white font-semibold">
                          {AUTO_RECOVERY_LABELS[r.reason] || r.reason}
                        </div>
                        <div className="font-mono text-[11px] text-white/40">{r.reason}</div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-xs text-white/40">{relTime(r.last_at)}</span>
                        <span className="min-w-[3ch] text-right font-bold text-[#22C55E]">{r.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  Green = the watchdog kicked in and playback recovered. High counts here are HEALTHY (the fix is doing its job).
                </p>
              </section>
            )}

            {/* Auto-skips breakdown */}
            {(data.unrecoverable?.by_reason || []).length > 0 && (
              <section data-testid="forensics-unrec-section">
                <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4 text-[#FFB020]" />
                  Auto-skipped tracks (3 failed resumes)
                </h3>
                <div className="rounded-xl border border-[#FFB020]/20 divide-y divide-white/5 overflow-hidden bg-[#FFB020]/[0.03]">
                  {data.unrecoverable.by_reason.map((r) => (
                    <div key={r.reason} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white font-semibold">
                          {UNRECOVERABLE_LABELS[r.reason] || r.reason}
                        </div>
                        <div className="font-mono text-[11px] text-white/40">{r.reason}</div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-xs text-white/40">{relTime(r.last_at)}</span>
                        <span className="min-w-[3ch] text-right font-bold text-[#FFB020]">{r.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  Amber = the watchdog gave up after 3 tries and skipped the track. Check the "Recent stall events" section below for the offending videoIds — they're probably deleted / geoblocked / embed-disabled.
                </p>
              </section>
            )}

            {/* Real stall reasons — genuine unrecovered failures */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FF3B3B]" />
                Real stalls by reason
              </h3>
              {(data.stalls?.by_reason || []).length === 0 ? (
                <p className="text-sm text-white/40">No unrecovered stalls in this window.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                  {data.stalls.by_reason.map((r) => (
                    <div key={r.reason} className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                      <span className="font-mono text-sm text-white">{r.reason}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-white/40">{relTime(r.last_at)}</span>
                        <span className="min-w-[3ch] text-right font-bold text-[#FF3B3B]">{r.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Ad detections */}
            <section data-testid="forensics-ads-section">
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <ShieldOff className="w-4 h-4 text-[#FF007F]" />
                Ad detections ({data.ads?.total ?? 0})
              </h3>
              {(data.ads?.by_track || []).length === 0 ? (
                <p className="text-sm text-white/40">
                  No YouTube ads slipped through in this window. The Topic-only filter is doing its job.
                </p>
              ) : (
                <>
                  <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden mb-3">
                    <div className="px-4 py-2 bg-white/[0.04] text-[10px] uppercase tracking-widest text-white/50">
                      Top ad-heavy tracks
                    </div>
                    {data.ads.by_track.map((t) => (
                      <div key={t.video_id} className="flex items-center justify-between px-4 py-3 bg-white/[0.02] gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white font-semibold truncate">
                            {t.track_name || t.video_id || '(unknown track)'}
                          </div>
                          <div className="text-[11px] text-white/40 truncate">
                            {t.track_artists || '—'} · {t.video_id}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className="text-xs text-white/40">{relTime(t.last_at)}</span>
                          <span className="min-w-[3ch] text-right font-bold text-[#FF007F]">{t.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(data.ads?.recent || []).length > 0 && (
                    <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[300px] overflow-y-auto">
                      <div className="sticky top-0 px-4 py-2 bg-[#0D0D0D] text-[10px] uppercase tracking-widest text-white/50 z-10">
                        Recent events
                      </div>
                      {data.ads.recent.map((a, i) => (
                        <div key={`${a.at || ''}-${a.requested_video_id || ''}-${i}`} className="px-4 py-2 bg-white/[0.02]">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white truncate mr-3">
                              {a.track_name || a.requested_video_id || '(unknown)'}
                            </span>
                            <span className="text-[11px] text-white/40 whitespace-nowrap">{relTime(a.at)}</span>
                          </div>
                          {a.track_artists && (
                            <div className="text-[11px] text-white/40 mt-0.5 truncate">{a.track_artists}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Devices */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-white/60" />
                Devices seen
              </h3>
              {(data.devices || []).length === 0 ? (
                <p className="text-sm text-white/40">No device UAs recorded.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                  {data.devices.map((d, i) => (
                    <div key={`${d.user_agent || 'unknown'}-${i}`} className="px-4 py-3 bg-white/[0.02]">
                      <div className="text-sm font-semibold text-white">{fmtUA(d.user_agent)}</div>
                      <div className="text-[11px] text-white/40 mt-0.5 font-mono truncate">{d.user_agent}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {d.sample_count} sample{d.sample_count === 1 ? '' : 's'} · last {relTime(d.last_seen)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Recent stall events */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-white/60" />
                Recent stall events ({data.stalls?.recent?.length || 0})
              </h3>
              {(data.stalls?.recent || []).length === 0 ? (
                <p className="text-sm text-white/40">No recent stalls.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                  {data.stalls.recent.map((s, i) => (
                    <div key={`${s.at || ''}-${s.reason || ''}-${i}`} className="px-4 py-3 bg-white/[0.02]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-[#FF3B3B]">{s.reason}</span>
                        <span className="text-[11px] text-white/40">{relTime(s.at)}</span>
                      </div>
                      {s.is_hidden && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-widest bg-[#FFB020]/15 text-[#FFB020] px-2 py-0.5 rounded">
                          Hidden tab
                        </span>
                      )}
                      {s.meta && Object.keys(s.meta).length > 0 && (
                        <pre className="text-[11px] text-white/50 font-mono mt-2 whitespace-pre-wrap break-all">
{JSON.stringify(s.meta, null, 0)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Frontend errors */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Bug className="w-4 h-4 text-white/60" />
                Recent JS errors ({data.errors?.recent?.length || 0})
              </h3>
              {(data.errors?.recent || []).length === 0 ? (
                <p className="text-sm text-white/40">No JS errors caught by the boundary. 👍</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                  {data.errors.recent.map((e, i) => (
                    <div key={`${e.at || ''}-${(e.message || '').slice(0, 40)}-${i}`} className="px-4 py-3 bg-white/[0.02]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#FFB020] truncate mr-3">{e.message}</span>
                        <span className="text-[11px] text-white/40 whitespace-nowrap">{relTime(e.at)}</span>
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5">at {e.path || '—'}</div>
                      {e.stack_head && (
                        <pre className="text-[11px] text-white/40 font-mono mt-2 whitespace-pre-wrap break-all">{e.stack_head}</pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminForensicsPage;
